import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import Confetti from 'react-confetti';
import { CheckCircle, Loader2, XCircle, Home, ArrowRight } from 'lucide-react';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { api, updateUser } = useAuth();
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (pollCount >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await api.get(`/subscriptions/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setPaymentDetails(data);
        setStatus('success');
        setShowConfetti(true);
        
        // Update user context
        updateUser({ subscription_plan: 'basic' }); // Will be updated properly from API
        
        setTimeout(() => setShowConfetti(false), 5000);
      } else if (data.status === 'expired') {
        setStatus('error');
      } else {
        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (error) {
      console.error('Payment status check error:', error);
      setPollCount(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-sky-50 to-white">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-sky-500 mx-auto mb-4" />
          <p className="text-lg text-slate-600">Confirming your payment...</p>
          <p className="text-sm text-slate-400 mt-2">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-red-50 to-white p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="font-fredoka text-2xl font-bold text-slate-900 mb-2">
              Payment Issue
            </h1>
            <p className="text-slate-600 mb-6">
              We couldn't confirm your payment. If you were charged, please contact support.
            </p>
            <div className="flex flex-col gap-3">
              <Link to="/pricing">
                <Button className="w-full" data-testid="try-again-btn">
                  Try Again
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" className="w-full" data-testid="back-to-dashboard-btn">
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white p-4">
      {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
      
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md"
      >
        <Card>
          <CardContent className="p-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-12 h-12 text-green-500" />
            </motion.div>
            
            <h1 className="font-fredoka text-3xl font-bold text-slate-900 mb-2">
              Welcome to Premium!
            </h1>
            
            <p className="text-lg text-slate-600 mb-6">
              Your subscription is now active. Enjoy all the premium features!
            </p>
            
            {paymentDetails && (
              <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                <p className="text-sm text-slate-500 mb-1">Amount Paid</p>
                <p className="font-bold text-slate-800">
                  ${paymentDetails.amount?.toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                </p>
              </div>
            )}
            
            <div className="flex flex-col gap-3">
              <Link to="/dashboard">
                <Button className="w-full h-12 text-lg bg-sky-500 hover:bg-sky-600" data-testid="go-to-dashboard-btn">
                  <Home className="w-5 h-5 mr-2" /> Go to Dashboard
                </Button>
              </Link>
              <Link to="/child/new">
                <Button variant="outline" className="w-full h-12" data-testid="add-child-btn">
                  Add Your First Child <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
